import React from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';

function formatRewardType(type) {
    if (type === 'visits') {
        return 'Visits';
    }
    if (type === 'points') {
        return 'Points';
    }
    if (type === 'birthday') {
        return 'Birthday';
    }
    return type ? String(type) : '-';
}

/**
 * RewardList - Displays list of rewards in table (desktop) and card (mobile) views
 * @param {Object} props
 * @param {Array} props.rewards - Array of reward objects
 * @param {Function} props.isEnabled - Function to check if reward type is enabled
 * @param {Function} props.onDelete - Callback when delete button is clicked
 * @param {number} props.currentPage - Current page number
 * @param {number} props.totalPages - Total number of pages
 * @param {Function} props.onPageChange - Callback when page changes
 */
export function RewardList({ rewards, isEnabled, onDelete, canDelete = true, currentPage, totalPages, onPageChange }) {
    const handleDeleteClick = (rewardId) => {
        onDelete(rewardId);
    };

    return (
        <div className="flex-1 bg-white shadow-md rounded-xl p-4 sm:p-6 overflow-x-auto">
            <h2 className="text-xl sm:text-2xl font-bold text-primary mb-4">Rewards List</h2>
            {/* 🖥 Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
                <table className="w-full table-auto text-sm">
                    <thead className="bg-gray-100 text-gray-600">
                        <tr>
                            {canDelete && <th className="p-3 w-10"></th>}
                            <th className="p-3 text-left">Type</th>
                            <th className="p-3 text-left">Title</th>
                            <th className="p-3 text-left">Description</th>
                            <th className="p-3 text-left">Cost</th>
                            {/* Max Redemption — hidden until implemented (see RewardForm max redemption UI) */}
                            {/* <th className="p-3 text-left">Max Redemption</th> */}
                        </tr>
                    </thead>
                    <tbody>
                        {rewards.map((r) => (
                            <tr
                                key={r.id}
                                className={`border-t ${!isEnabled(r.type) ? 'bg-gray-100 text-gray-400' : ''}`}
                            >
                                {canDelete && (
                                <td className="p-3 text-center">
                                    <button
                                        onClick={() => handleDeleteClick(r.id)}
                                        className={`p-1 rounded hover:bg-gray-100 ${!isEnabled(r.type) ? 'text-gray-400 cursor-not-allowed' : 'text-red-600'}`}
                                        disabled={!isEnabled(r.type)}
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </td>
                                )}
                                <td className="p-3">{formatRewardType(r.type)}</td>
                                <td className="p-3">{r.title}</td>
                                <td className="p-3 text-xs italic text-gray-600">
                                    {r.description || '-'}
                                </td>
                                <td className="p-3">{r.cost || '-'}</td>
                                {/* <td className="p-3">{r.max_redemptions || '-'}</td> */}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {totalPages > 1 && (
                    <div className="flex justify-center items-center mt-6 space-x-2 text-sm">
                        <button
                            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                        >
                            Prev
                        </button>
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                onClick={() => onPageChange(i + 1)}
                                className={`px-3 py-1 rounded border ${
                                    currentPage === i + 1 ? 'bg-primary text-white' : 'text-gray-700'
                                }`}
                            >
                                {i + 1}
                            </button>
                        ))}
                        <button
                            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* 📱 Mobile Card View */}
            <div className="block md:hidden space-y-4">
                {rewards.map((r) => (
                    <div
                        key={r.id}
                        className={`rounded-lg border p-4 shadow-sm ${!isEnabled(r.type) ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-800'}`}
                    >
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-semibold text-primary">
                                {formatRewardType(r.type)}
                            </span>
                            {canDelete && (
                            <button
                                onClick={() => handleDeleteClick(r.id)}
                                disabled={!isEnabled(r.type)}
                                className={`text-red-600 ${!isEnabled(r.type) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <TrashIcon className="h-4 w-4" />
                            </button>
                            )}
                        </div>
                        <p className="text-base font-medium">{r.title}</p>
                        <div className="mt-2 text-sm">
                            <p>
                                <span className="font-medium">Cost:</span> {r.cost || '-'}
                            </p>
                            {/* Max redemption — hidden until implemented */}
                            {/* <p>
                                <span className="font-medium">Max:</span> {r.max_redemptions || '-'}
                            </p> */}
                        </div>
                    </div>
                ))}
                {totalPages > 1 && (
                    <div className="flex justify-center items-center mt-6 space-x-2 text-sm">
                        <button
                            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                        >
                            Prev
                        </button>
                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                onClick={() => onPageChange(i + 1)}
                                className={`px-3 py-1 rounded border ${
                                    currentPage === i + 1 ? 'bg-primary text-white' : 'text-gray-700'
                                }`}
                            >
                                {i + 1}
                            </button>
                        ))}
                        <button
                            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

